'use client';
import { Box, Skeleton, type SxProps, type Theme } from '@mui/material';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { ClearAllButton } from '@repo/ui/OffersFilter/ClearAllButton';
import { SortByDropdown } from '@repo/ui/OffersFilter/SortByDropdown';
import { StickyBar } from '@repo/ui/OffersFilter/StickyBar';
import { useFilterEvents } from '@repo/ui/OffersFilter/useFilterEvents';
import { useShowStickyBar } from '@repo/ui/OffersFilter/useShowStickyBar';
import { type RefObject, startTransition, useCallback, useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { SelectedFilterPills } from '../SelectedFilterPills';
import { useOffersFilterStore } from '../store';
import { BonusSelect } from './BonusSelect';
import { getFilterConfig } from './filterConfig';
import { LocationSelect } from './LocationSelect';
import { MinBalanceSelect } from './MinBalanceSelect';
import { MinDepositSelect } from './MinDepositSelect';
import { ProductTypeSelect } from './ProductTypeSelect';
import { TermLengthSelect } from './TermLengthSelect';

export type DesktopFiltersProps = {
  initialLocation?: StateAbbreviation;
  regionCode?: StateAbbreviation;
  mainSectionRef: RefObject<HTMLDivElement | null>;
  slug: string;
  analyticsProps: {
    actionSection: string;
    pageIdentifier: string;
  };
  onClearFilters?: () => void;
  initialClientRegion?: string;
  showFilterSkeleton?: boolean;
  isBankBonusesFilterExperiment?: boolean;
  scrollTargetRef?: RefObject<HTMLElement | null>;
};

export const DesktopFilters = ({
  mainSectionRef,
  slug,
  initialLocation,
  regionCode,
  analyticsProps,
  onClearFilters,
  initialClientRegion,
  showFilterSkeleton,
  isBankBonusesFilterExperiment,
  scrollTargetRef,
}: DesktopFiltersProps) => {
  const filterBoxRef = useRef<HTMLDivElement>(null);
  const showStickBar = useShowStickyBar(filterBoxRef, mainSectionRef);

  const { sortBySelectEvent, clearAllFiltersEvent } = useFilterEvents({
    actionSection: analyticsProps.actionSection,
    pageIdentifier: analyticsProps.pageIdentifier,
  });

  const { sortOptions, switchBarFilters, filtersApplied, appliedFilters, sortBy, handleClearFilters } =
    useOffersFilterStore(
      ({ sortOptions, switchBarFilters, filtersApplied, appliedFilters, sortBy, handleClearFilters }) => ({
        sortOptions,
        switchBarFilters,
        filtersApplied,
        appliedFilters,
        sortBy,
        handleClearFilters,
      }),
      shallow,
    );

  const numberOfAppliedFilters = isBankBonusesFilterExperiment
    ? (appliedFilters
        ? Object.values(appliedFilters).reduce((acc: number, currentValue: string | string[]) => {
            if (Array.isArray(currentValue)) return acc + currentValue.length;
            if (currentValue === initialClientRegion) return acc;
            return acc + (currentValue ? 1 : 0);
          }, 0)
        : 0)
    : 0;
  const hasActiveSelections = isBankBonusesFilterExperiment
    ? numberOfAppliedFilters > 0 || !!sortBy
    : filtersApplied || !!sortBy;

  useEffect(() => {
    switchBarFilters();
  }, [showStickBar]);

  const scrollBackToMainBar = () => {
    const target = scrollTargetRef?.current ?? filterBoxRef.current;
    if (target) {
      const y = target.getBoundingClientRect().top + window.scrollY - 15;
      window.scrollTo({ top: y, behavior: 'instant' });
    }
  };

  const renderFilter = (
    componentName: string,
    isStickyBar = false,
    isBankBonusesExperiment?: boolean,
  ) => {
    switch (componentName) {
      case 'ProductTypeSelect':
        return (
          <ProductTypeSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            regionCode={regionCode}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      case 'LocationSelect':
        if (!initialLocation) {
          return null;
        }
        return (
          <LocationSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      case 'TermLengthSelect':
        return (
          <TermLengthSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      case 'MinDepositSelect':
        return (
          <MinDepositSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      case 'MinBalanceSelect':
        return (
          <MinBalanceSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      case 'BonusSelect':
        return (
          <BonusSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            isBankBonusesFilterExperiment={isBankBonusesExperiment}
            {...analyticsProps}
          />
        );
      default:
        return null;
    }
  };

  const renderFilters = useCallback(
    (isStickyBar = false) => {
      const { filters } = getFilterConfig(slug);

      if (showFilterSkeleton) {
        // Add a skeleton for SortByDropdown when this profile has sort options.
        return Array.from({ length: filters.length + (sortOptions.length > 0 ? 1 : 0) }).map(
          (_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              sx={{ minHeight: '40px', width: { sm: '150px' } }}
            />
          ),
        );
      }

      return (
        <>
          {filters.map(({ component }) =>
            renderFilter(component, isStickyBar, isBankBonusesFilterExperiment),
          )}
          {sortOptions.length > 0 && (
            <SortByDropdown
              sortOptions={sortOptions}
              useStore={useOffersFilterStore}
              filterKey="Sort By"
              sortBySelectEvent={sortBySelectEvent}
              isBankBonusesFilterExperiment={isBankBonusesFilterExperiment}
              {...(isStickyBar && { isStickyBar, scrollBackToMainBar })}
            />
          )}
        </>
      );
    },
    [
      showFilterSkeleton,
      slug,
      sortOptions,
      scrollBackToMainBar,
      sortBySelectEvent,
      isBankBonusesFilterExperiment,
    ],
  );

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        flexDirection: 'column',
        gap: isBankBonusesFilterExperiment ? 3.5 : 2,
        pt: isBankBonusesFilterExperiment ? 0 : 3,
      }}
      ref={filterBoxRef}
    >
      <Box
        sx={
          [
            {
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'end' },
              flexGrow: 1,
              gap: isBankBonusesFilterExperiment ? 3 : 2,
            },
            ...(isBankBonusesFilterExperiment ? [{ '& > *:not(:last-child)': { flex: 1 } }] : []),
          ] as SxProps<Theme>
        }
      >
        {renderFilters()}
        {isBankBonusesFilterExperiment && (
          <ClearAllButton
            onClick={() => {
              setTimeout(() => {
                startTransition(() => {
                  (onClearFilters ?? handleClearFilters)();
                  clearAllFiltersEvent();
                });
              }, 100);
            }}
            disabled={!hasActiveSelections}
          />
        )}
      </Box>
      <SelectedFilterPills
        isMobile={false}
        analyticsProps={analyticsProps}
        onClearFilters={onClearFilters}
        regionCode={regionCode}
        isBankBonusesFilterExperiment={isBankBonusesFilterExperiment}
      />
      {showStickBar && (
        <StickyBar
          showStickyBar={showStickBar}
          useStore={useOffersFilterStore}
          scrollBackToMainBar={scrollBackToMainBar}
          onClearFilters={onClearFilters}
          clearAllFiltersEvent={clearAllFiltersEvent}
          initialClientRegion={initialClientRegion}
          isBankBonusesFilterExperiment={isBankBonusesFilterExperiment}
        >
          {renderFilters(true)}
        </StickyBar>
      )}
    </Box>
  );
};
